import cn from 'classnames';
import { FormikProps } from 'formik';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { OnChangeValue } from 'react-select';

import { ReactComponent as AccountIcon } from '../../../assets/images/icon-input-account.svg';
import { ReactComponent as EmailIcon } from '../../../assets/images/icon-input-mail.svg';
import { NO_DEPARTMENT } from '../../../constants/organizations';
import {
  ADDRESS1,
  ADDRESS2,
  ADMIN_TYPE,
  AVAILABLE_BUDGET,
  BUDGET_CURRENCY,
  CITY,
  COUNTRY,
  CRM_SKIP_ALLOWED,
  DEPARTMENT_ID,
  DISPLAY_CURRENCY,
  DISPLAY_CURRENCY_HINT,
  DISTRIBUTOR,
  DISTRIBUTOR_ORG_IDS,
  EMAIL,
  FIRST_NAME,
  LAST_NAME,
  ORG_ADMIN,
  PHONE,
  ROLES_SELECT_OPTIONS,
  SKIP_WELCOME_EMAIL,
  STATE,
  SUPER_ADMIN,
  UID,
  ZIP,
} from '../../../constants/users';
import useWindowSize from '../../../hooks/useWindowSize';
import { selectAdminType, selectCurrentUserId } from '../../../store/selectors/auth';
import {
  selectCurrentOrganizationData,
  selectCurrentOrganizationId,
  selectIsUserBudgetEnabled,
  selectOrganizationList,
} from '../../../store/selectors/organizations';
import { IMaintenanceFormOutputData, MaintenanceFormStateEnum } from '../../../types/forms';
import { ISelectorValue } from '../../../types/shell';
import { IUser } from '../../../types/users';
import { hasPermission } from '../../../utils/users';
import { HelpTooltip } from '../../tooltips';
import { CountrySelector, DepartmentSelect, Input, InputLabel, OrganizationSelect, Selector, Toggle } from '../index';
import { CurrencySelector, MoneyInput } from '../inputs';

import styles from './UserForm.module.scss';

interface IProps extends IMaintenanceFormOutputData {
  onChange: (name: keyof IUser, value: string | number | undefined | string[] | boolean) => void;
  form: FormikProps<Partial<IUser>>;
  disabled?: boolean;
  requiredFields: Record<string, boolean>;
}

const UserForm = ({ onChange, disabled, form, mode, requiredFields }: IProps) => {
  const adminType = useSelector(selectAdminType);
  const accountOrgId = useSelector(selectCurrentOrganizationId);
  const { mobile } = useWindowSize();
  const organizations = useSelector(selectOrganizationList);
  const currentUserId = useSelector(selectCurrentUserId);
  const { options: organizationOptions } = useSelector(selectCurrentOrganizationData) || {};
  const isSkipCrmAllowedInOrg = React.useMemo(() => organizationOptions?.crm_skip_allowed, [organizationOptions]);
  const isUserBudgetEnabled = useSelector(selectIsUserBudgetEnabled);

  const isReadOnly = React.useMemo(() => mode === MaintenanceFormStateEnum.Reading, [mode]);
  const isEditing = React.useMemo(() => mode === MaintenanceFormStateEnum.Editing, [mode]);
  const isAdding = React.useMemo(() => mode === MaintenanceFormStateEnum.Adding, [mode]);

  const roleOptions = React.useMemo(() => {
    /* Only super can add distributor to the system
     * Possibility to edit distributor should be handled in other place
     */
    const showDistributorRole = form.values[ADMIN_TYPE] === DISTRIBUTOR || hasPermission([SUPER_ADMIN], adminType);

    return ROLES_SELECT_OPTIONS(adminType).filter((role) => {
      return role.value === DISTRIBUTOR ? showDistributorRole : true;
    });
  }, [adminType, form.values[ADMIN_TYPE]]);

  const isDisabledUserSelectorRole = React.useMemo(() => {
    return isEditing && hasPermission(DISTRIBUTOR, adminType) && form.values[UID] === currentUserId;
  }, [isEditing, adminType, currentUserId, form.values[UID]]);

  const getFieldErrors = React.useCallback(
    (fieldName: keyof IUser) => {
      return form.touched[fieldName] && form.errors[fieldName] ? form.errors[fieldName] : undefined;
    },
    [form.touched, form.errors],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const { name, value } = e.currentTarget;
      if (!name) {
        console.warn('No field name was specified');
        return;
      }

      if (typeof onChange === 'function') {
        onChange(name as keyof IUser, value);
      }
    },
    [onChange],
  );

  const handleToggleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked: value } = e.currentTarget;
      if (!name) {
        console.warn('No field name was specified');
        return;
      }

      if (typeof onChange === 'function') {
        onChange(name as keyof IUser, value);
      }
    },
    [onChange],
  );

  const selectedRole = React.useMemo(() => form?.values[ADMIN_TYPE], [form?.values?.[ADMIN_TYPE]]);
  const selectedDepartment = React.useMemo(() => form?.values[DEPARTMENT_ID], [form?.values?.[DEPARTMENT_ID]]);
  const distributorOrgIds = React.useMemo(
    () => form?.values?.[DISTRIBUTOR_ORG_IDS],
    [form.values[DISTRIBUTOR_ORG_IDS]],
  );

  /**
   * This effect watches the `selectedRole` for higher admin roles.
   * It sets `no_department` to higher admin roles if it was set by any reason before,
   * i.e. the user's role was raised from department admin to org_admin
   * FIXME: it should be validated on the BE side as well
   */
  React.useEffect(() => {
    if (
      hasPermission([ORG_ADMIN, SUPER_ADMIN, DISTRIBUTOR], selectedRole ?? '') &&
      selectedDepartment !== NO_DEPARTMENT
    ) {
      onChange(DEPARTMENT_ID, NO_DEPARTMENT);
    }
  }, [onChange, selectedRole, selectedDepartment]);

  /**
   * This effect watches the `selectedRole` for Distributor role.
   * 1. For all the rest roles it clears the `distributorOrgIds` array.
   * 2. For the Distributor role it adds the current org to the `distributorOrgIds` array.
   * FIXME: it should be validated on the BE side as well
   */
  React.useEffect(() => {
    if (selectedRole !== DISTRIBUTOR && distributorOrgIds?.length) {
      onChange(DISTRIBUTOR_ORG_IDS, []);
    }

    if (selectedRole === DISTRIBUTOR) {
      if (accountOrgId && !distributorOrgIds?.includes(accountOrgId)) {
        onChange(DISTRIBUTOR_ORG_IDS, distributorOrgIds?.concat([accountOrgId]));
      }
    }
  }, [onChange, accountOrgId, selectedRole, distributorOrgIds]);

  const handleSelectInputChange = React.useCallback(
    (name: keyof IUser, selected?: OnChangeValue<ISelectorValue, boolean> | null) => {
      const { value } = (selected as ISelectorValue) || {};
      return handleChange({ currentTarget: { name, value } } as unknown as
        | React.ChangeEvent<HTMLInputElement>
        | React.MouseEvent<HTMLButtonElement, MouseEvent>);
    },
    [handleChange],
  );

  const formatAdminTypeValue = React.useCallback(
    (value: string): ISelectorValue => ({
      label: roleOptions.find((role) => role.value === value)?.label || '',
      value,
    }),
    [roleOptions],
  );

  const orgLabels = React.useMemo(() => {
    return organizations && distributorOrgIds?.length
      ? distributorOrgIds?.map((orgId) => organizations.find(({ uid }) => uid === orgId)?.name).join(', ')
      : null;
  }, [organizations, distributorOrgIds]);

  return (
    <React.Fragment>
      <form className={styles.container} onSubmit={form.handleSubmit}>
        <div className={styles.section}>
          <div className={styles.subtitle}>Permissions settings</div>
          <Selector
            containerClassName={styles.inputContainer}
            helperText="Assign role"
            onChange={(value) => (isReadOnly ? null : handleSelectInputChange(ADMIN_TYPE, value))}
            options={roleOptions}
            value={formatAdminTypeValue(form?.values[ADMIN_TYPE] || '')}
            isMulti={false}
            isSearchable={false}
            isClearable={false}
            closeMenuOnSelect
            readOnly={isReadOnly || isDisabledUserSelectorRole}
          />
          {!hasPermission([SUPER_ADMIN, ORG_ADMIN, DISTRIBUTOR], form?.values[ADMIN_TYPE] ?? '') && (
            <DepartmentSelect
              containerClassName={styles.inputContainer}
              helperText="Choose department"
              isMulti={false}
              readOnly={isReadOnly}
              onChange={handleChange}
              value={form.values[DEPARTMENT_ID]}
              onBlur={form.handleBlur}
              error={form.touched[DEPARTMENT_ID] ? form.errors[DEPARTMENT_ID] : undefined}
              className={styles.input}
              name={DEPARTMENT_ID}
              isClearable={false}
              closeMenuOnSelect
            />
          )}
          {hasPermission([DISTRIBUTOR, SUPER_ADMIN], adminType) &&
            hasPermission([DISTRIBUTOR], form?.values[ADMIN_TYPE] ?? '') &&
            ((isReadOnly || isEditing) && !hasPermission([SUPER_ADMIN], adminType) ? (
              <div className={styles.orgCount}>
                <InputLabel value="Organizations" />
                <div className={styles.organizationsList}>
                  {form.values[DISTRIBUTOR_ORG_IDS]?.length}{' '}
                  {form.values[DISTRIBUTOR_ORG_IDS]?.length === 1 ? `organization` : `organizations`}
                  <HelpTooltip
                    id="controls-info"
                    offset={{ right: mobile ? 0 : 10 }}
                    className={styles.tooltip}
                    data-border
                    data-tip
                  >
                    {orgLabels}
                  </HelpTooltip>
                </div>
              </div>
            ) : (
              <OrganizationSelect
                containerClassName={styles.inputContainer}
                helperText="Choose organizations"
                isMulti
                isSearchable
                readOnly={isReadOnly}
                onChange={handleChange}
                concreteOption={accountOrgId}
                value={form.values[DISTRIBUTOR_ORG_IDS]}
                onBlur={() => {
                  form.setFieldTouched(DISTRIBUTOR_ORG_IDS);
                }}
                error={form.touched[DISTRIBUTOR_ORG_IDS] ? form.errors[DISTRIBUTOR_ORG_IDS] : undefined}
                className={styles.input}
                name={DISTRIBUTOR_ORG_IDS}
              />
            ))}
        </div>
        <div className={styles.section}>
          <div className={styles.subtitle}>Profile information</div>
          <Input
            field={form.getFieldProps(EMAIL)}
            name={EMAIL}
            placeholder="Email"
            helperText="Email"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[EMAIL]}
            value={form.values[EMAIL]}
            onChange={handleChange}
            error={getFieldErrors(EMAIL)}
            readOnly={isReadOnly || isEditing}
            icon={<EmailIcon />}
          />
          <Input
            field={form.getFieldProps(FIRST_NAME)}
            name={FIRST_NAME}
            placeholder="First name"
            helperText="First name"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[FIRST_NAME]}
            value={form.values[FIRST_NAME]}
            onChange={handleChange}
            error={getFieldErrors(FIRST_NAME)}
            readOnly={isReadOnly}
            icon={<AccountIcon />}
          />
          <Input
            field={form.getFieldProps(LAST_NAME)}
            name={LAST_NAME}
            placeholder="Last name"
            helperText="Last name"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[LAST_NAME]}
            value={form.values[LAST_NAME]}
            onChange={handleChange}
            error={getFieldErrors(LAST_NAME)}
            readOnly={isReadOnly}
            icon={<AccountIcon />}
          />
          <Input
            field={form.getFieldProps(ADDRESS1)}
            name={ADDRESS1}
            placeholder="Address 1"
            helperText="Address 1"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[ADDRESS1]}
            value={form.values[ADDRESS1]}
            onChange={handleChange}
            error={getFieldErrors(ADDRESS1)}
            readOnly={isReadOnly}
          />
          <Input
            field={form.getFieldProps(ADDRESS2)}
            name={ADDRESS2}
            placeholder="Address 2"
            helperText="Address 2"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[ADDRESS2]}
            value={form.values[ADDRESS2]}
            onChange={handleChange}
            error={getFieldErrors(ADDRESS2)}
            readOnly={isReadOnly}
          />
          <Input
            field={form.getFieldProps(CITY)}
            name={CITY}
            placeholder="City"
            helperText="City"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[CITY]}
            value={form.values[CITY]}
            onChange={handleChange}
            error={getFieldErrors(CITY)}
            readOnly={isReadOnly}
          />
          <Input
            field={form.getFieldProps(STATE)}
            name={STATE}
            placeholder="State"
            helperText="State"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[STATE]}
            value={form.values[STATE]}
            onChange={handleChange}
            error={getFieldErrors(STATE)}
            readOnly={isReadOnly}
          />
          <Input
            field={form.getFieldProps(ZIP)}
            name={ZIP}
            placeholder="ZIP code"
            helperText="ZIP code"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[ZIP]}
            value={form.values[ZIP]}
            onChange={handleChange}
            error={getFieldErrors(ZIP)}
            readOnly={isReadOnly}
          />
          <CountrySelector
            containerClassName={styles.inputContainer}
            className={cn({ [styles.errorBorder]: form.touched[COUNTRY] && form.errors[COUNTRY] })}
            label="Country"
            name={COUNTRY}
            required={requiredFields[COUNTRY]}
            value={form.values[COUNTRY]}
            onChange={handleSelectInputChange}
            error={getFieldErrors(COUNTRY)}
            disabled={disabled}
          />
          <Input
            field={form.getFieldProps(PHONE)}
            name={PHONE}
            placeholder="Phone"
            helperText="Phone"
            className={styles.inputContainer}
            disabled={disabled}
            required={requiredFields[PHONE]}
            value={form.values[PHONE]}
            onChange={handleChange}
            error={getFieldErrors(PHONE)}
            readOnly={isReadOnly}
          />
          {isUserBudgetEnabled && (
            <>
              <MoneyInput
                currency={form.values[BUDGET_CURRENCY]}
                amount={form.values[AVAILABLE_BUDGET]}
                className={styles.inputContainer}
                onChange={onChange}
                names={{ currency: BUDGET_CURRENCY, amount: AVAILABLE_BUDGET }}
                helperText="Available budget"
                readOnly={isReadOnly}
                isDisabled={disabled}
                onBlur={form.handleBlur}
                errorClassName={styles.error}
                isReadOnly={isReadOnly}
                isRequired={requiredFields[AVAILABLE_BUDGET]}
                error={getFieldErrors(AVAILABLE_BUDGET)}
              />
              <CurrencySelector
                label="Display currency"
                onChange={(value: string) => onChange(DISPLAY_CURRENCY, value)}
                hint={DISPLAY_CURRENCY_HINT}
                value={form.values[DISPLAY_CURRENCY]}
                name={DISPLAY_CURRENCY}
              />
            </>
          )}
          <div className={styles.toggleGroup}>
            {isSkipCrmAllowedInOrg && (
              <Toggle
                field={form.getFieldProps(CRM_SKIP_ALLOWED)}
                name={CRM_SKIP_ALLOWED}
                className={styles.inputContainer}
                disabled={isReadOnly}
                hint={
                  <React.Fragment>
                    This option allows user to skip CRM integration
                    <br />
                    and only manually type in the shipment information.
                  </React.Fragment>
                }
                helperText="Allow skip CRM"
                onChange={handleToggleChange}
                checked={!!form.values?.[CRM_SKIP_ALLOWED]}
              />
            )}
            {isAdding && (
              <Toggle
                field={form.getFieldProps(SKIP_WELCOME_EMAIL)}
                name={SKIP_WELCOME_EMAIL}
                className={styles.inputContainer}
                hint="When enabled, this user won't receive the one-time welcome email. Other emails, such as password reset, are unaffected."
                helperText="Don't send welcome email"
                onChange={handleToggleChange}
                checked={!!form.values?.[SKIP_WELCOME_EMAIL]}
              />
            )}
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};

export default UserForm;
